import React, { useState } from 'react';
import { AlertTriangle, RotateCcw, X } from 'lucide-react';
import useGameStore from '../store/gameStore';

/**
 * LegacySaveBanner — bandeau bloquant affiché en haut de l'app lorsque la
 * sauvegarde Zustand vient d'être migrée depuis une version pré-F1/F4.
 *
 * Contexte (04/02/2026) :
 * - La refonte F1-F4 (formations, chauffeurs, flotte, dépôt) introduit des
 *   schémas de données structurellement incompatibles avec les saves v1.
 * - Choix produit validé : pas de migration douce automatique. On invite
 *   l'utilisateur à recommencer.
 *
 * Le flag __legacySave est posé par le middleware persist.migrate (cf.
 * gameStore.js). Au clic « Recommencer une partie », on vide localStorage
 * et on recharge la page (retour à l'écran d'activation).
 */
export default function LegacySaveBanner() {
  const isLegacy = useGameStore((s) => s.__legacySave);
  const [dismissed, setDismissed] = useState(false);

  if (!isLegacy || dismissed) return null;

  const handleReset = () => {
    try {
      window.localStorage.removeItem('hst-game-save');
    } catch (_) { /* ignore */ }
    window.location.reload();
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[200] bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg"
      data-testid="legacy-save-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">
            Votre sauvegarde provient d'une version antérieure de SCD.
          </p>
          <p className="text-xs text-amber-50/90 mt-0.5">
            Suite à la refonte profonde des formations, des chauffeurs, de la flotte
            et du dépôt, les anciennes parties ne sont plus compatibles. Merci de
            démarrer une nouvelle partie pour profiter du nouveau contenu.
          </p>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/95 text-amber-700 hover:bg-white font-bold text-xs uppercase tracking-wide shadow-md transition-colors"
          data-testid="legacy-save-reset"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Recommencer une partie
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-white/70 hover:text-white transition-colors"
          data-testid="legacy-save-dismiss"
          aria-label="Masquer cette alerte"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
